/**
 * Approvals — the realtime pending queue is now `users` where
 * `status == 'pending'` (the separate registration_requests collection is
 * retired). Approve/Reject are DIRECT updateDoc writes on the user doc
 * (admins may write users per the security rules):
 *   - Approve → { status: 'approved', approvedAt: Date.now() }
 *   - Reject  → { status: 'rejected' }
 * Rows are optimistically disabled while the write is in flight; the realtime
 * listener then drops the row when its status leaves 'pending'.
 */
import { useEffect, useState } from 'react';
import { onSnapshot, setDoc } from 'firebase/firestore';
import { appConfigDoc } from '../lib/collections';
import { usePendingUsers } from '../hooks/useFirestore';
import { useApproveUser, useRejectUser } from '../hooks/useAdminActions';
import { Card, SectionHeader, Button, Badge } from '../components/ui';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { formatDateTime, timeAgo } from '../lib/format';
import type { User } from '@mitang/shared';

export function ApprovalsPage(): JSX.Element {
  const pending = usePendingUsers();
  const approve = useApproveUser();
  const reject = useRejectUser();

  // Track which user is being acted on (optimistic row disable).
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectFor, setRejectFor] = useState<User | null>(null);
  const [reason, setReason] = useState('');

  const onApprove = (u: User) => {
    setBusyId(u.id);
    approve.mutate(
      { uid: u.id, name: u.name },
      { onSettled: () => setBusyId(null) },
    );
  };

  const onConfirmReject = () => {
    if (!rejectFor) return;
    const u = rejectFor;
    setBusyId(u.id);
    reject.mutate(
      { uid: u.id, name: u.name },
      {
        onSettled: () => {
          setBusyId(null);
          setRejectFor(null);
          setReason('');
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Approvals"
        subtitle="New dealer registrations awaiting review."
        actions={<Badge tone="warn">{pending.data.length} pending</Badge>}
      />

      <ApprovalToggle />

      {pending.loading ? (
        <LoadingState label="Loading requests…" />
      ) : pending.error ? (
        <ErrorState message={pending.error} />
      ) : pending.data.length === 0 ? (
        <EmptyState
          title="No pending requests"
          hint="New registrations from the dealer app will appear here in realtime."
        />
      ) : (
        <div className="space-y-3">
          {pending.data.map((u) => {
            const isBusy = busyId === u.id;
            return (
              <Card key={u.id} className="p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-slate-900">{u.name}</h3>
                      <Badge tone="neutral">{u.companyName}</Badge>
                    </div>
                    <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                      <Field label="Mobile" value={u.mobile} mono />
                      <Field label="Email" value={u.email} />
                      <Field label="City" value={u.city} />
                      <Field label="GST" value={u.gstNumber ?? '—'} mono />
                      <Field label="Requested" value={formatDateTime(u.createdAt)} />
                      <Field label="Age" value={timeAgo(u.createdAt)} />
                    </dl>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      variant="success"
                      onClick={() => onApprove(u)}
                      loading={isBusy && approve.isPending}
                      disabled={isBusy}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => {
                        setRejectFor(u);
                        setReason('');
                      }}
                      disabled={isBusy}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Reject reason modal */}
      {rejectFor && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => !reject.isPending && setRejectFor(null)}
            aria-hidden
          />
          <div className="relative w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-slate-900">Reject registration</h3>
            <p className="mt-1 text-sm text-slate-500">
              Rejecting <span className="font-medium">{rejectFor.name}</span> ({rejectFor.companyName}).
            </p>
            <label htmlFor="reason" className="mt-4 block text-xs font-medium text-slate-600">
              Reason (optional — for your reference only)
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              placeholder="e.g. Could not verify GST number."
            />
            <p className="mt-1 text-[11px] text-slate-400">
              Note: the reason is not stored — the shared user model has no such field. Rejecting
              only sets <code className="rounded bg-slate-100 px-1">status: rejected</code>.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setRejectFor(null)}
                disabled={reject.isPending}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={onConfirmReject} loading={reject.isPending}>
                Confirm reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className={`text-slate-700 ${mono ? 'font-mono text-[13px]' : ''}`}>{value}</dd>
    </div>
  );
}


/**
 * Business toggle: app_config/global.approvalRequired. OFF → new dealers get
 * product access immediately after OTP login and the 3-field profile
 * (company, mobile, city — no Skip);
 * ON → the classic pending → approve flow. Enforced by the security rules.
 */
function ApprovalToggle(): JSX.Element {
  const [required, setRequired] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(appConfigDoc, (snap) => {
      setRequired(snap.data()?.approvalRequired ?? true);
    });
    return unsub;
  }, []);

  const flip = async () => {
    if (required === null || saving) return;
    setSaving(true);
    try {
      await setDoc(appConfigDoc, { approvalRequired: !required }, { merge: true });
    } finally {
      setSaving(false);
    }
  };

  if (required === null) return <></>;
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            Dealer approval: {required ? 'REQUIRED' : 'OFF'}
          </p>
          <p className="text-xs text-slate-500">
            {required
              ? 'New dealers wait in this queue until you approve them.'
              : 'New dealers get access immediately after login and filling in company, mobile and city. Blocked dealers stay blocked.'}
          </p>
        </div>
        <Button variant={required ? 'primary' : 'secondary'} onClick={() => void flip()} disabled={saving}>
          {saving ? 'Saving…' : required ? 'Turn approval OFF' : 'Turn approval ON'}
        </Button>
      </div>
    </Card>
  );
}
